import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
} from 'react';
import { CSSObject } from 'styled-components';
import { MenuStyled } from './menu.styled';
import Portal from '../Portal';

interface MenuProps {
  placement?: 'bottom-end' | 'overlap' | 'right-bottom' | 'left-bottom';
  children?: React.ReactNode | React.ReactNode[];
  anchorEl: HTMLElement;
  onClose: () => void;
  onClick?: () => void;
  open: boolean;
  sx?: CSSObject;
}

const Menu = ({
  open,
  onClick,
  onClose = () => {},
  placement = 'bottom-end',
  anchorEl,
  children,
  ...rest
}: MenuProps) => {
  const [position, setPosition] = useState({});
  const modalRef = useRef<HTMLDivElement | null>(null);
  // TODO create hook -> useCoordinates
  const setMenuCoordinates = () => {
    if (anchorEl !== null && modalRef.current !== null) {
      const {
        top,
        bottom,
        left,
        right,
        width,
        height,
      } = anchorEl.getBoundingClientRect();
      const { width: modalWidth } = modalRef.current.getBoundingClientRect();
      const modalStyle = getComputedStyle(modalRef.current);

      // set position on top of anchor element
      const topPosition = `calc(${top + height}px - ${
        modalStyle.borderWidth
      } + ${window.scrollY}px)`;

      if (placement === 'bottom-end') {
        setPosition({
          left: `calc(${left - modalWidth + width}px + ${
            modalStyle.borderWidth
          })`,
          top: topPosition,
        });
      } else if (placement === 'left-bottom') {
        setPosition({
          left: `calc(${right}px - ${modalStyle.borderWidth})`,
          top: topPosition,
        });
      } else if (placement === 'overlap') {
        setPosition({
          left,
          top: top + window.scrollY,
        });
      } else if (placement === 'right-bottom') {
        setPosition({
          left: `calc(${right}px - ${modalStyle.borderWidth})`,
          top: `calc(${bottom}px - ${modalStyle.height} + ${modalStyle.borderWidth} + ${window.scrollY}px)`,
        });
      }
    }
  };

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [open],
  );

  useLayoutEffect(() => setMenuCoordinates(), [anchorEl, open]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleOutsideClick = useCallback(
    (event: any) => {
      if (
        open &&
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        anchorEl &&
        !anchorEl.contains(event.target)
      ) {
        onClose();
      }
    },
    [open],
  );

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick, true);

    return () => {
      document.removeEventListener('click', handleOutsideClick, true);
    };
  }, [handleOutsideClick]);

  if (open) {
    return (
      <Portal>
        <MenuStyled onClick={onClick} ref={modalRef} style={position} {...rest}>
          {children}
        </MenuStyled>
      </Portal>
    );
  }

  return null;
};

export default Menu;
